import { trigger, state, style, transition, animate } from '@angular/animations';
import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, FormGroupDirective, NgForm, FormsModule, FormGroup, FormControl } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { combineLatest, of, Subscription } from 'rxjs';
import { concatAll, delay, filter, map, mergeMap, take, tap, toArray } from 'rxjs/operators';
import { CalculateDialogComponent } from '../../pump-simulation/calculate-dialog/calculate-dialog.component';
import { UserLocalStorage } from '../../login/user-local-store';
import { compareArray, removeRadioBtnFocus } from '../../shared/util';
import { DirtyComponent } from '../dirty-component';
import { DisplayProductDataService } from '../display-product-data.service';
import { ErrorButtonComponent } from '../error-button/error-button.component';
import { PUMPSYSYTEM_ITEMS, PUMP_SYSTEM_STATUS, PUMP_SYSTEM_TYPES, ConnectedProductDetail, AccessoryDetail, CalculationDetail, CALCULATIONS_ITEMS, SelectedCalculationDetail, PORTFOLIO_RIGHTS } from '../pump-data.model';
import { SaveButtonComponent } from '../save-button/save-button.component';
import { setAccessorySelectedList, setAccessoryPrepareList } from '../store/accessory/accessory.actions';
import { AccessoryState } from '../store/accessory/accessory.reducer';
import { selectAccessory } from '../store/accessory/accessory.selector';
import { setCalculationValue, setCoefficientSetValueName, setPumpSystemCopied } from '../store/coefficient-set/coefficient-set.actions';
import { CoefficientSetState, emptyCalculationValue } from '../store/coefficient-set/coefficient-set.reducer';
import { selectCoefficientSet } from '../store/coefficient-set/coefficient-set.selector';
import { setConnectedProductSelectedList, setConnectedProductPrepareList } from '../store/connected-product/connected-product.actions';
import { ConnectedProductState } from '../store/connected-product/connected-product.reducer';
import { selectConnectedProduct } from '../store/connected-product/connected-product.seletor';
import { ControllerState } from '../store/controller/controller.reducer';
import { selectController } from '../store/controller/controller.seletor';
import { PackagingState } from '../store/packaging/packaging.reducer';
import { selectPackaging } from '../store/packaging/packaging.selector';
import { setControllerSelect, setInitFormValue, setMotorSelect, setPumpEndSelect, setPumpSytemCoefficientSetName, setPumpUnitValue } from '../store/pump-system/pump-system.actions';
import { PumpSystemState } from '../store/pump-system/pump-system.reducer';
import { selectPumpSystem } from '../store/pump-system/pump-system.seletor';
import { setPumpUnitItem, setPumpUnitMaxTemp, setPumpUnitSuctionHead, setPumpUnitPicture, setPumpUnitPackaging, setPumpUnitDrawing, setPumpUnitDrawingUrl, setPumpUnitPictureUrl } from '../store/pump-unit/pump-unit.actions';
import { PumpUnitState } from '../store/pump-unit/pump-unit.reducer';
import { selectPumpUnit } from '../store/pump-unit/pump-unit.seletor';
import { UpdateButtonComponent } from '../update-button/update-button.component';
import { PumpSystemItem } from './pump-system.model';
import { PUMP_SYSTEM_NO_RANGE_KEY, fieldNoRangeKeyForController } from '../../number_ranges/number-range-keys';

/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: UntypedFormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Component({
  selector: 'app-pump-systems',
  templateUrl: './pump-systems.component.html',
  styleUrls: ['./pump-systems.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  standalone: false
})
export class PumpSystemsComponent implements OnInit, DirtyComponent, OnDestroy, AfterViewInit {

  private subscriptions: Subscription[] = [];
  showSubmitLoading = false;
  @Input() dataSource = new MatTableDataSource();
  @ViewChild(MatPaginator, { static: true })
  paginator: MatPaginator = new MatPaginator;
  matcher = new MyErrorStateMatcher();
  userLocalStorage = new UserLocalStorage();
  private formValueChange: Subscription = new Subscription();
  types: string[] = PUMP_SYSTEM_TYPES;
  portfolio_rights: string[] = PORTFOLIO_RIGHTS;
  displayedColumns: string[] = ['approveForUse', 'version', 'status', 'published', 'author', 'comment', 'action'];
  pumpSystemForm!: FormGroup;
  originalPumpSystemFormValue: any;
  originalConnectedProductValue: any[] = [];
  originalConnectedProductValueBeforeCopied: any[] = [];
  originalAccessoryValue: any[] = [];
  originalAccessoryValueBeforeCopied: any[] = [];
  pumpSystemUuid = '';

  isDirty = false;
  isVersionValueChanged = false;
  pumpSystemName = '';
  tableLoading = true;
  showVersionTable = true;
  showPumpSystemForm = false;
  pumpSystemData: PumpSystemItem[] = [];
  selectedRowIndex = -1;
  typeSelect = false;
  statusList = PUMP_SYSTEM_STATUS;
  defaultAccessoryList: Partial<AccessoryDetail>[] = [];
  accessoryList: Partial<AccessoryDetail>[] = [];
  defaultConnectedProductList: Array<{
    version_number: any; file: string; uuid: string; version: number
  }> = [];
  connectedProductsList: Partial<ConnectedProductDetail>[] = [];

  calculationsValue!: Partial<CalculationDetail> | Partial<SelectedCalculationDetail>;
  controllerNameValue = '';
  readonly noRangeKey = PUMP_SYSTEM_NO_RANGE_KEY;
  fieldNoRangeKey: string | null = null;
  motorNameNameValue = '';
  pumpEndNameValue = '';
  pumpUnitValues: any = {};
  pumpEndUuid: string = '';
  controllerUuid: string = '';
  motorUuid: string = '';

  constructor(
    private store: Store,
    private router: Router,
    private fb: UntypedFormBuilder,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private elementRef: ElementRef,
    private displayProductData: DisplayProductDataService,
  ) { }
  ngAfterViewInit(): void {
    const group: { [key: string]: any } = {};
    PUMPSYSYTEM_ITEMS.forEach(key => {
      group[key] = ''; // just a string, not an array
    });
    this.pumpSystemForm = this.fb.group(group);
  }

  async ngOnInit() {
    const group: { [key: string]: any } = {};
    PUMPSYSYTEM_ITEMS.forEach(key => {
      group[key] = ''; // just a string, not an array
    });
    this.pumpSystemForm = this.fb.group(group);

    const pumpUnit = combineLatest([
      this.store.pipe(
        select(selectPumpUnit),
        map((data: PumpUnitState) => data.item)
      ),
      this.store.pipe(
        select(selectPumpUnit),
        map((data: PumpUnitState) => data.maxTemp)
      ),
      this.store.pipe(
        select(selectPumpUnit),
        map((data: PumpUnitState) => data.suctionHead)
      ),
      this.store.pipe(
        select(selectPumpUnit),
        map((data: PumpUnitState) => data.picture)
      ),
      this.store.pipe(
        select(selectPumpUnit),
        map((data: PumpUnitState) => data.drawing)
      ),
      this.store.pipe(
        select(selectPumpUnit),
        map((data: PumpUnitState) => data.drawing_url)
      ),
      this.store.pipe(
        select(selectPumpUnit),
        map((data: PumpUnitState) => data.picture_url)),

      this.store.pipe(
        select(selectPackaging),
        map((data: PackagingState) => data.data)
      ),
      this.store.pipe(
        select(selectPumpUnit),
        map((data: PumpUnitState) => data.packaging)
      )
    ]).subscribe(([itemValue, maxTempValue, suctionHeadValue, pictureValue, drawingValue, drawingUrl, pictureUrl, packagingData, packagingValue]): void => {
      this.pumpUnitValues.pu_item = itemValue;
      this.pumpUnitValues.pu_max_temp = maxTempValue;
      this.pumpUnitValues.pu_suction_head = suctionHeadValue;
      this.pumpUnitValues.pu_picture = pictureValue;
      this.pumpUnitValues.pu_pictureUrl = pictureUrl;
      this.pumpUnitValues.pu_drawing_url = drawingUrl;
      this.pumpUnitValues.pu_drawing = drawingValue;
      const item = [...packagingData].find(item => item.name === packagingValue);
      if (item) {
        this.pumpUnitValues.pu_packaging = item;
      } else {
        this.pumpUnitValues.pu_packaging = {
          uuid: '',
          name: '',
          weight: '',
          subType: {
            H: '',
            L: '',
            W: ''
          }
        }
      }
      const pumpUnit: any = {
        item: this.pumpUnitValues.pu_item,
        pu_max_temp: this.pumpUnitValues.pu_max_temp,
        parts: this.pumpUnitValues.pu_parts,
        picture: this.pumpUnitValues.pu_picture ? this.pumpUnitValues.pu_picture : this.pumpUnitValues.pu_drawing,
        drawing: this.pumpUnitValues.pu_drawing ? this.pumpUnitValues.pu_drawing : this.pumpUnitValues.pu_picture,
        drawing_url: this.pumpUnitValues.pu_drawing_url,
        picture_url: this.pumpUnitValues.pu_pictureUrl,
        suction_head: this.pumpUnitValues.pu_suction_head,
        packaging: this.pumpUnitValues.pu_packaging
      };
      this.pumpUnitValues.pu_unit = pumpUnit;
      this.store.dispatch(setPumpUnitValue({ data: pumpUnit }));

    });
    this.subscriptions.push(pumpUnit);

    const names = combineLatest([
      this.store.pipe(
        select(selectController),
        map((data: ControllerState) => data.controller)
      ),
      this.store.pipe(
        select(selectController),
        map((data: ControllerState) => data.motor)
      ),
      this.store.pipe(
        select(selectController),
        map((data: ControllerState) => data.pumpEnd)
      )
    ]).subscribe(([controller, motor, pumpEnd]) => {
      this.controllerNameValue = `${controller.name}`;
      this.fieldNoRangeKey = fieldNoRangeKeyForController(this.controllerNameValue);
      this.motorNameNameValue = `${motor.name}`;
      this.pumpEndNameValue = `${pumpEnd.name}`;
      this.controllerUuid = `${controller.uuid}`;
      this.pumpEndUuid = `${pumpEnd.uuid}`;
      this.motorUuid = `${motor.uuid}`;
    });
    this.subscriptions.push(names);

    const getCalculation = combineLatest([
      this.store.pipe(
        select(selectCoefficientSet),
        map((data: CoefficientSetState) => data.calculation),
      ),
      this.store.pipe(
        select(selectCoefficientSet),
        map((data: CoefficientSetState) => data.name)
      ),
      this.store.pipe(
        select(selectPumpSystem),
        map((data: PumpSystemState) => [data.isFormValueChanged, data.coefficientSetValue])
      ),
    ]).subscribe(([data, name, isChanged]) => {
      this.calculationsValue = { ...data };
      this.calculationsValue.displayName = `${name}`;
      this.calculationsValue.filename = `${isChanged[1]}`;
      isChanged[0] ? this.isDirty = true : this.isDirty = false;
    });
    this.subscriptions.push(getCalculation);

    const connectedProductTableData = combineLatest([
      this.store.pipe(
        select(selectConnectedProduct),
        map((data: ConnectedProductState) => data.selectedList)
      ),
      this.store.pipe(
        select(selectConnectedProduct),
        map((data: ConnectedProductState) => data.isCopied)
      )
    ]).subscribe(([data, isCopied]) => {
      if (data.length !== 0 && !isCopied) {
        this.connectedProductsList = data;
        const connectedData = this.connectedProductsList.map(data => data.uuid).filter((uuid): uuid is string => uuid !== undefined);
        const difference = compareArray(connectedData, this.originalConnectedProductValue);
        this.isDirty = difference.length !== 0;
      }

      if (data.length === 0 && !isCopied) {
        this.isDirty = this.originalConnectedProductValue.length !== 0;
      }

      if (isCopied) {
        this.connectedProductsList = data;
        const connectedData = this.connectedProductsList.map(data => data.uuid);
        const difference = compareArray(connectedData.filter((uuid): uuid is string => uuid !== undefined), this.originalConnectedProductValueBeforeCopied);
        this.isDirty = difference.length !== 0;
      }
    });
    this.subscriptions.push(connectedProductTableData);

    const accessoryTableData = combineLatest([
      this.store.pipe(
        select(selectAccessory),
        map((data: AccessoryState) => data.selectedList)
      ),
      this.store.pipe(
        select(selectAccessory),
        map((data: AccessoryState) => data.isCopied)
      )
    ]).subscribe(([data, isCopied]) => {
      if (data.length !== 0 && !isCopied) {
        this.accessoryList = data;
        const accessoryData = this.accessoryList.map(data => data.uuid);
        const difference = compareArray(accessoryData.filter((uuid): uuid is string => uuid !== undefined), this.originalAccessoryValue);
        this.isDirty = difference.length !== 0;
      }

      if (data.length === 0 && !isCopied) {
        this.isDirty = this.originalAccessoryValue.length !== 0;
      }

      if (isCopied) {
        this.accessoryList = data;
        const accessoryData = this.accessoryList.map(data => data.uuid);
        const difference = compareArray(accessoryData.filter((uuid): uuid is string => uuid !== undefined), this.originalAccessoryValueBeforeCopied);
        this.isDirty = difference.length !== 0;
      }
    })
    this.subscriptions.push(accessoryTableData);

    const urlSubscription = this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.showVersionTable = true;
      this.pumpSystemName = '';
      this.showPumpSystemForm = false;
      this.selectedRowIndex = -1;
      this.isDirty = false;
      this.defaultAccessoryList = [];
      this.defaultConnectedProductList = [];
      this.showSubmitLoading = false;
    });
    this.subscriptions.push(urlSubscription);

    const getAllData = this.route.paramMap.pipe(
      map(params => params.get('uuid')),
      mergeMap(uuid => {
        return combineLatest([
          this.displayProductData.getPumpSystemItem(uuid!),
          this.displayProductData.getConnectedProductList().pipe(
            take(1),
            concatAll(),
            mergeMap(item => this.displayProductData.getConnectedProductItem(item.uuid)),
            toArray()
          ),
          this.displayProductData.getAccessoryList()
        ]);
      })
    ).subscribe(([pumpSystemData, connectedProductData, accessoryData]) => {

      const pumpSystemDataApi = JSON.parse(pumpSystemData['message']);
      this.pumpSystemName = pumpSystemDataApi.name;
      this.pumpSystemUuid = pumpSystemDataApi.uuid;
      this.portfolio_rights = pumpSystemDataApi.portfolio_rights;
      this.tableLoading = false;
      this.showVersionTable = false;
      this.dataSource.data = pumpSystemDataApi.versions;
      this.pumpSystemData = pumpSystemDataApi.versions;

      for (const item of connectedProductData) {
        const tartget = item.versions[0];
        tartget.file = item.name;
        tartget.uuid = item.uuid;
        tartget.version = item.versions[0].version_number;
        this.defaultConnectedProductList.push(tartget);
      }

      for (const item of accessoryData) {
        let tartget: any = { ...item };
        tartget.file = item.name;
        this.defaultAccessoryList.push(tartget);
      }

    });
    this.subscriptions.push(getAllData);

  }

  async showVersionData(version: string) {
    if (this.formValueChange) {
      this.formValueChange.unsubscribe();
    }

    if (!this.isVersionValueChanged) {
      // the form value not changed
      await this.setPumpSystemFormValue(Number(version));
    } else {
      const dialogRef = this.dialog.open(UpdateButtonComponent, {
        width: '600px',
        disableClose: true,
        minHeight: '20vh',
      });
      dialogRef.afterClosed().subscribe(async result => {
        if (result) {
          this.isDirty = false;
          await this.setPumpSystemFormValue(Number(version));
        }
      });
    }
  }

  showSelectedVersionRadioBtn(version: number) {
    if (this.showPumpSystemForm && (version === this.selectedRowIndex)) {
      const dialogRef = this.dialog.open(CalculateDialogComponent, {
        width: '600px',
        disableClose: true,
        minHeight: '20vh',
        data: {
          message: 'Are you sure you want to publish this version?',
          type: 'confirm'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          const data = this.pumpSystemData[version];
          const user = this.userLocalStorage.loadUser();
          data.author = user?.user?.username || 'Unknown';
          data.comments = data.comments;
          const url = `${this.pumpSystemUuid}/${version}/live`;
          const setPumpSystemState = this.displayProductData.setPumpSystemState(url, data).subscribe(item => {
            this.snackBar
              .open(JSON.parse(item)['message'], '', { duration: 1500, panelClass: 'hintMsg' })
              .afterDismissed()
              .pipe(delay(0))
              .subscribe(() => {
                this.ngOnInit();
                this.showVersionTable = true;
                this.pumpSystemName = '';
                this.showPumpSystemForm = false;
                this.selectedRowIndex = -1;
                this.isDirty = false;
                this.defaultAccessoryList = [];
                this.defaultConnectedProductList = [];
              });
          });

          this.subscriptions.push(setPumpSystemState);
        } else {
          removeRadioBtnFocus(this.elementRef, version);
        }
      });
    } else {
      const dialogRef = this.dialog.open(CalculateDialogComponent, {
        width: '600px',
        disableClose: true,
        minHeight: '20vh',
        data: {
          message: 'You have to load a version before publishing it.',
          type: 'hint'
        }
      });

      dialogRef.afterClosed().subscribe(() => removeRadioBtnFocus(this.elementRef, version));
    }

  }

  async setPumpSystemFormValue(version: number): Promise<void> {
    this.selectedRowIndex = Number(version);
    this.showPumpSystemForm = true;

    // Step 1: Initialize empty form group
    const group: { [key: string]: any } = {};
    PUMPSYSYTEM_ITEMS.forEach(key => group[key] = '');
    this.pumpSystemForm = this.fb.group(group);

    if (!this.pumpSystemForm.get('portfolio_rights')) {
      this.pumpSystemForm.addControl('portfolio_rights', new FormControl(''));
    }

    // Step 2: Find matching data by version
    const allData = this.pumpSystemData.find(d => d.version_number === version);
    if (!allData) return;

    const pumpSystemFormData: any = {};
    const allDataRecord = allData as Record<string, any>;
    PUMPSYSYTEM_ITEMS.forEach(name => pumpSystemFormData[name] = allDataRecord[name] || '');

    // Step 3: Handle pump unit details
    const pumpUnit = allData.pu_unit;
    const pumpUnitValue: any = {
      item: pumpUnit?.item || '',
      packaging: '',
      maxTemp: pumpUnit?.pu_max_temp || '',
      suctionHead: pumpUnit?.suction_head || '',
      picture: pumpUnit?.picture || '',
      drawing: pumpUnit?.drawing || '',
      drawing_url: pumpUnit?.drawing_url || '',
      picture_url: pumpUnit?.picture_url || ''
    };
    if (pumpUnit) {
      this.store.dispatch(setPumpUnitItem({ data: pumpUnitValue.item }));
      this.store.dispatch(setPumpUnitMaxTemp({ data: pumpUnitValue.maxTemp }));
      this.store.dispatch(setPumpUnitSuctionHead({ data: pumpUnitValue.suctionHead }));
      this.store.dispatch(setPumpUnitPicture({ data: pumpUnitValue.picture }));
      this.store.dispatch(setPumpUnitDrawing({ data: pumpUnitValue.drawing }));
      this.store.dispatch(setPumpUnitDrawingUrl({ data: pumpUnitValue.drawing_url }));
      this.store.dispatch(setPumpUnitPictureUrl({ data: pumpUnitValue.picture_url }));

      const packaging = pumpUnit.packaging;
      if (packaging) {
        pumpUnitValue.packaging = packaging.name || `${packaging.l} (${packaging.h}x${packaging.w})-${packaging.pack_weight}`;
        this.store.dispatch(setPumpUnitPackaging({ data: pumpUnitValue.packaging }));
      }

      this.store.dispatch(setPumpUnitValue({ data: pumpUnitValue }));
    }

    // Step 4: Coefficient + initial form values
    const { calculations, controller, motor, pump_end, portfolio_rights } = allData;
    const coefficientSetName = calculations?.displayName || '';
    const coefficientSetFilename = calculations?.filename || '';

    if (controller && motor && pump_end && calculations && portfolio_rights) {
      this.store.dispatch(setInitFormValue({
        data: {
          pumpUnit: pumpUnitValue,
          controllerUuid: controller.uuid,
          motorUuid: motor.uuid,
          pumpEndUuid: pump_end.uuid,
          coefficientSetValue: coefficientSetName,
          pumpSystemCoefficientSetName: coefficientSetFilename,
          portfolio_rights
        }
      }));
    }

    if (calculations) {
      this.store.dispatch(setCalculationValue({ data: calculations }));
      this.store.dispatch(setCoefficientSetValueName({ data: coefficientSetName }));
      this.store.dispatch(setPumpSytemCoefficientSetName({ data: coefficientSetFilename }));
      this.store.dispatch(setPumpSystemCopied({ data: true }));
    }

    // Step 5: Connected products
    this.handleConnectedProducts(allData.connectedProducts);

    // Step 6: Accessories
    this.handleAccessories(allData.accessories);

    // Step 7: UUID selections
    pumpSystemFormData.controller_uuid = controller?.uuid || '';
    pumpSystemFormData.motor_uuid = motor?.uuid || '';
    pumpSystemFormData.pump_end_uuid = pump_end?.uuid || '';

    if (controller) this.store.dispatch(setControllerSelect({ data: controller.uuid }));
    if (motor) this.store.dispatch(setMotorSelect({ data: motor.uuid }));
    if (pump_end) this.store.dispatch(setPumpEndSelect({ data: pump_end.uuid }));

    this.portfolio_rights = portfolio_rights;
    const cleanGroup: { [key: string]: FormControl } = {};
    for (const key of Object.keys(pumpSystemFormData)) {
      cleanGroup[key] = new FormControl(pumpSystemFormData[key]); // or add validators here if needed
    }
    this.pumpSystemForm = this.fb.group(cleanGroup);
    this.originalPumpSystemFormValue = this.pumpSystemForm.value;

    // Step 8: Watch form changes
    this.formValueChange = this.pumpSystemForm.valueChanges.pipe(
      tap(value => {
        this.typeSelect = this.types.includes(value.type);
        this.portfolio_rights = value.portfolio_rights;
        //this.pumpSystemForm.patchValue({ portfolio_rights: value.portfolio_rights });
      })
    ).subscribe(value => {
      const isChanged = JSON.stringify(this.originalPumpSystemFormValue) !== JSON.stringify(value);
      this.isDirty = isChanged;
      this.isVersionValueChanged = isChanged;
    });

    this.subscriptions.push(this.formValueChange);
  }

  private handleConnectedProducts(connectedProducts: any[]) {
    if (connectedProducts) {
      this.originalConnectedProductValue = connectedProducts.map(x => x.uuid);
      this.originalConnectedProductValueBeforeCopied = [...this.originalConnectedProductValue];
      this.store.dispatch(setConnectedProductSelectedList({ data: connectedProducts }));

      const targetUUIDs = connectedProducts.map(x => x.uuid);
      const prepareList = this.defaultConnectedProductList.filter(item => !targetUUIDs.includes(item.uuid));
      this.store.dispatch(setConnectedProductPrepareList({ data: prepareList }));
    } else {
      this.originalConnectedProductValue = [];
      this.originalConnectedProductValueBeforeCopied = [];
      this.store.dispatch(setConnectedProductSelectedList({ data: [] }));
      this.store.dispatch(setConnectedProductPrepareList({ data: this.defaultConnectedProductList }));
    }
  }

  private handleAccessories(accessories: any[]) {
    if (accessories) {
      this.originalAccessoryValue = accessories.map(x => x.uuid);
      this.originalAccessoryValueBeforeCopied = [...this.originalAccessoryValue];
      this.store.dispatch(setAccessorySelectedList({ data: accessories }));

      const targetUUIDs = accessories.map(x => x.uuid);
      const prepareList = this.defaultAccessoryList.filter(item => !targetUUIDs.includes(item.uuid));
      this.store.dispatch(setAccessoryPrepareList({ data: prepareList }));
    } else {
      this.originalAccessoryValue = [];
      this.originalAccessoryValueBeforeCopied = [];
      this.store.dispatch(setAccessorySelectedList({ data: [] }));
      this.store.dispatch(setAccessoryPrepareList({ data: this.defaultAccessoryList }));
    }
  }


  selectVersionItem(version: number) {
    if (this.showPumpSystemForm && (version === this.selectedRowIndex)) {
      const dialogRef = this.dialog.open(CalculateDialogComponent, {
        width: '600px',
        disableClose: true,
        minHeight: '20vh',
        data: {
          message: 'Are you sure you want to publish this version?',
          type: 'confirm'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          const data = this.pumpSystemData[version];
          const user = this.userLocalStorage.loadUser();
          data.author = user!.user.username;
          data.comment = data.comments;
          const url = `${this.pumpSystemUuid}/${version}/live`;
          const setPumpSystemState = this.displayProductData.setPumpSystemState(url, data).subscribe(item => {
            this.snackBar
              .open(JSON.parse(item)['message'], '', { duration: 1500, panelClass: 'hintMsg' })
              .afterDismissed()
              .pipe(delay(0))
              .subscribe(() => {
                this.ngOnInit();
                this.showVersionTable = true;
                this.pumpSystemName = '';
                this.showPumpSystemForm = false;
                this.selectedRowIndex = -1;
                this.isDirty = false;
              });
          });

          this.subscriptions.push(setPumpSystemState);
        } else {
          removeRadioBtnFocus(this.elementRef, version);
        }
      });
    } else {
      const dialogRef = this.dialog.open(CalculateDialogComponent, {
        width: '600px',
        disableClose: true,
        minHeight: '20vh',
        data: {
          message: 'You have to load a version before publishing it.',
          type: 'hint'
        }
      });

      dialogRef.afterClosed().subscribe(() => removeRadioBtnFocus(this.elementRef, version));
    }

  }

  onSave() {
    const dialogRef = this.dialog.open(SaveButtonComponent, {
      width: '600px',
      disableClose: true,
      minHeight: '20vh',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.showSubmitLoading = true;
        this.updatePumpSystem(result['comment']);
      }
    });
  }

  updatePumpSystem(versionComment: string) {
    let dataUpdated = false;
    let data = this.pumpSystemForm.value;
    data.header = '';
    data.row = '';
    const user = this.userLocalStorage.loadUser();
    data.author = user!.user.username;
    data.comment = versionComment;
    data.uuid = this.pumpSystemUuid;
    data.name = this.pumpSystemName;
    data.controller = this.controllerNameValue;
    data.motor_name = this.motorNameNameValue;
    data.pump_end_name = this.pumpEndNameValue;
    data.controller_uuid = this.controllerUuid;
    data.pump_end_uuid = this.pumpEndUuid;
    data.motor_uuid = this.motorUuid;
    data.portfolio_rights = this.portfolio_rights;


    data = { ...data, ...this.pumpUnitValues };

    const calculationData: any = {};
    CALCULATIONS_ITEMS.forEach(name => {
      if ((this.calculationsValue as any)[name]) {
        calculationData[name] = (this.calculationsValue as any)[name];
      } else {
        calculationData[name] = '';
      }
    });
    data.calculations = calculationData;

    const connectedProducts: { name: string; version: any }[] = [];
    if (this.connectedProductsList && this.connectedProductsList.length !== 0) {
      const connectedUuid = this.connectedProductsList.map(x => x.uuid);
      this.defaultConnectedProductList.forEach(item => {
        if (connectedUuid.includes(item.uuid)) {
          const subItem: any = {};
          subItem.name = item.file;
          subItem.version = item.version_number;
          connectedProducts.push(subItem);
        }
      });
      data.connectedProducts = connectedProducts;
    }
    const accessories: Partial<AccessoryDetail>[] = [];
    if (this.accessoryList && this.accessoryList.length !== 0) {
      const accessoryData = of(this.accessoryList).pipe(
        concatAll(),
        mergeMap(item => item.uuid ? this.displayProductData.getAccessoryItem(item.uuid) : of(null)),
        filter((result): result is AccessoryDetail => result !== null),
        toArray()
      )
        .subscribe(result => {
          for (const item of result) {
            const tartget = { ...item.versions[0] };
            tartget.name = item.name;
            tartget.version = item.versions[0].version_number;
            accessories.push(tartget);
          }
          data.accessories = accessories;
          if (!dataUpdated) {
            console.log('updated data 1');
            this.updatePumpSystemWithData(data);
            dataUpdated = true;
          }
        });
      this.subscriptions.push(accessoryData);
    } else {
      // this.accessoryList value is undefined
      if (!dataUpdated) {
        console.log('updated data 2');
        this.updatePumpSystemWithData(data);
        dataUpdated = true;
      }
    }
    if (!dataUpdated) {
      console.log('updated data 3');
      this.updatePumpSystemWithData(data);
      dataUpdated = true;
    }
  }

  updatePumpSystemWithData(data: any) {
    this.displayProductData.updatePumpSystem(data).subscribe(
      item => {
        this.snackBar.open(JSON.parse(item)['message'], '', { duration: 1500, panelClass: 'hintMsg' })
          .afterDismissed()
          .pipe(delay(0))
          .subscribe(() => {
            this.ngOnInit();
            this.showVersionTable = true;
            this.pumpSystemName = '';
            this.showPumpSystemForm = false;
            this.selectedRowIndex = -1;
            this.isDirty = false;
            this.defaultAccessoryList = [];
            this.defaultConnectedProductList = [];
            this.showSubmitLoading = false;
          });
      },
      error => {
        this.dialog.open(ErrorButtonComponent, {
          width: '600px',
          data: JSON.parse(error.error),
          minHeight: '20vh',
        });
        this.showSubmitLoading = false;
      },
      () => { }
    );
  }

  canDeactivate() {
    return this.isDirty;
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.store.dispatch(setCoefficientSetValueName({ data: 'Please select coefficient set' }));
    this.store.dispatch(setCalculationValue({ data: emptyCalculationValue }));
    this.store.dispatch(setCoefficientSetValueName({ data: '' }));
    this.store.dispatch(setPumpSytemCoefficientSetName({ data: '' }));
    this.store.dispatch(setPumpSystemCopied({ data: false }));
  }

}
